import { StatCard } from '../../ui/StatCard';
import { TableComponent } from '../../ui/TableComponent';
import { useAuth } from '../../../services/authentication/authContext';
import { GraphCard } from '../../ui/GraphCard';
import { graphPurpleColor, graphOrangeColor, getRankBadgeStyle } from '../../../styling/stylingHelper';
import { metricIcon, RevenueIcon, SpendIcon, ProductSpendIcon, WasteIcon } from '../../ui/metricIcons';
import { useMemo, useEffect, useState, type ReactNode } from 'react';
import { SideBarChart } from '../../ui/SideBarChart';
import { PriceAlertsCard } from '../../dashboard/PriceAlertsCard';
import { TopSection } from '../../ui/TopSection';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import { ResponsiveTable, MobileCardList, MobileCardField } from '../../shared/mobile/ResponsiveTable';
import RestaurantSelector from '../../common/RestaurantSelector';
import { RestaurantFilterDropdown } from '../../ui/RestaurantFilterDropdown';
import { useLoadOrderHistory, useLoadRevenueHistory, useLoadPriceAlerts, useLoadWasteNeeds } from '../../../utils/dataLoadingUtils';
import { useOrderHistory } from '../../../stores/appOrderHistoryStore';
import { useRevenueHistory } from '../../../stores/appRevenueHistoryStore';
import { usePriceAlertsStore } from '../../../stores/appPriceAlertsStore';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useAppUserDefaultStore, isAllRestaurantsSelected, saveVatSetting, saveVatOrdersSetting } from '../../../stores/appUserDefaultStore';
import { useFiltersStore } from '../../../stores/appFiltersStore';
import { filterOrdersByDateRange, filterRevenueByDateRange } from '../../../utils/dateRangeFilters';
import { calculateTotalSpent, calculateFullSupplierSpending, calculateCostGraphData } from '../../../utils/orderHistoryCalculations';
import { calculateTotalRevenue, calculateRevenueGraphData, calculateTopRevenueItems, calculateRevenueMix } from '../../../utils/revenueHistoryCalculations';
import { SPLIT_FOOD, SPLIT_DRINKS } from '../reports/hub/common';
import { currencyFormatter } from '../../../services/helperService';
import {
  calculateCostPercentage,
  calculateRevenueChange,
  calculateCostChange,
  calculateCostPercentageChange
} from '../../../utils/kpiCalculations';
import { transformPriceAlertsForCard, filterPriceAlertsByDateRange } from '../../../utils/priceAlertsCalculations';
import { filterOrdersByOverallType } from '../../orderhistory/utils';
import { filterRevenueByOverallType } from '../../revenuehistory/utils';
import { useTimeControls } from '../../../hooks/useTimeControls';
import { Navigate, useNavigate } from 'react-router-dom';
import { View } from '../../../components/orderhistory/types';
import { getProductAggregationKey } from '../../orderhistory/aggregations';
import { getProductPrice } from '../../orderhistory/vatUtils';
import { getProductQuantity } from '../../../utils/platformUtils';
import { useTranslation } from 'react-i18next';
import { RestaurantOverviewTable } from '../../dashboard/RestaurantOverviewTable';
import { RestaurantRevenuePieChart } from '../../dashboard/RestaurantRevenuePieChart';
import { RankingCard } from '../../dashboard/RankingCard';
import { ExpandModal } from '../../ui/ExpandModal';
import { NoDataInPeriod } from '../../ui/NoDataInPeriod';
import { useWasteNeedsStore } from '../../../stores/appWasteNeedsStore';
import { computeWasteTotalsFromNeeds } from '../../../utils/reports/utilizationReport';
import type { ReportRange } from '../../../utils/reports/shared';
import { calculateRestaurantOverview, calculateRestaurantRevenueBreakdown, calculateRestaurantCostBreakdown } from '../../../utils/allRestaurantsDashboardCalculations';
import BudgetLabel from '../../dashboard/BudgetLabel';
import { isCurrentWeek, calculatePastWeekDifference, calculateCurrentWeekBudget, BUDGET_TARGET_PERCENTAGE } from '../../../utils/budgetCalculations';
import { canAccessBudgetReminder, canAccessCostPercentPage, canAccessInventoryAdjustedCostPercent } from '../../../services/accessManagement/accessManagement';
import { useStockAdjustedCost } from '../../../hooks/useStockAdjustedCost';
import { stockAdjustedCostPercent, type SideStockAdjustment } from '../../../utils/inventoryAdjustedCostPercent';
import { StockAdjustedCost } from '../../dashboard/StockAdjustedCost';


const formatPct = (n: number) => `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
const wholeCurrencyFormatter = new Intl.NumberFormat('en-US', {
  ...currencyFormatter.resolvedOptions(),
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});


/** A row in a "view full ranking" modal: a name, a primary value, and an optional proportional bar + secondary text. */
interface RankingModalRow {
  name: string;
  value: string;
  barPct?: number;
  barColor?: string;
  sub?: string;
}

/** Left-aligned, stacked food/drinks legend rows (no bar) shown under a KPI value. */
const SplitLegend = ({ rows }: { rows: { color: string; label: string; value: string; extra?: ReactNode }[] }) => (
  <div className="flex flex-col gap-1.5 text-sm">
    {rows.map((row) => (
      <span key={row.label} className="flex items-center gap-1.5">
        <span className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: row.color }} />
        <span className="text-muted dark:text-muted-dark font-medium">{row.label}</span>
        <span className="text-textPrimary dark:text-textPrimary-dark font-semibold">{row.value}</span>
        {row.extra}
      </span>
    ))}
  </div>
);

const DashboardPage = ({ restaurantOverviewOnly = false }: { restaurantOverviewOnly?: boolean }) => {
  const { backendUser, accountSettings } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Connect to stores
  const orderHistoryStore = useOrderHistory();
  const revenueHistoryStore = useRevenueHistory();
  const priceAlertsStore = usePriceAlertsStore();
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const restaurants = useRestaurantStore(s => s.restaurants);
  const restaurantSectionsMap = useRestaurantStore(s => s.restaurantSections);
  const platformMap = useRestaurantStore(s => s.restaurantSectionPlatforms);
  const revenueSourcesMap = useRestaurantStore(s => s.restaurantRevenueSources);
  const showExVatOrders = useAppUserDefaultStore(s => s.showExVatOrders);
  const showExVatRevenue = useAppUserDefaultStore(s => s.showExVat);

  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);

  // Use centralized filter store
  const overallTypeFilter = useFiltersStore(s => s.overallTypeFilter);
  const setOverallTypeFilter = useFiltersStore(s => s.setOverallTypeFilter);
  const restaurantFilters = useFiltersStore(s => s.restaurantFilters);
  const setRestaurantFilters = useFiltersStore(s => s.setRestaurantFilters);

  // Reset filter to 'all' if current value is not available on this page
  useEffect(() => {
    const availableOptions = ['all', 'food', 'drinks'];
    if (overallTypeFilter && !availableOptions.includes(overallTypeFilter)) {
      setOverallTypeFilter('all');
    }
  }, [overallTypeFilter, setOverallTypeFilter]);

  // Time controls - centralized logic. Declared before the data loading hooks so the
  // progressive order-history loader can prioritise the currently-viewed period.
  const {
    startDate,
    endDate,
    selectedTimeOption,
    dateRange,
    setDateRange,
    setSelectedTimeOption,
  } = useTimeControls();

  // Load data. The all-restaurants order history can be huge (tens of thousands of
  // orders), so it loads progressively: a recent window first for a fast render, then
  // the rest streams in the background. Single-restaurant keeps the original full load.
  // Both order and revenue history load progressively for every restaurant (single or
  // all): a recent window renders first, then the rest streams in the background. This
  // keeps the dashboard fast even for restaurants with years of data.
  useLoadOrderHistory({ progressive: true, viewStart: startDate });
  useLoadRevenueHistory({ progressive: true, viewStart: startDate });
  useLoadPriceAlerts();
  // Waste-ranking card (all-restaurants only): load the period-independent per-unit
  // "needed ingredients" index once; waste per period is computed client-side below.
  useLoadWasteNeeds();

  // Get progressive-loading states from stores
  const ordersFirstChunkLoaded = useOrderHistory(s => s.firstChunkLoaded);
  const ordersFullyLoaded = useOrderHistory(s => s.fullyLoaded);
  const ordersLoadedFromDate = useOrderHistory(s => s.loadedFromDate);
  const revenueFirstChunkLoaded = useRevenueHistory(s => s.firstChunkLoaded);
  const revenueFullyLoaded = useRevenueHistory(s => s.fullyLoaded);
  const revenueLoadedFromDate = useRevenueHistory(s => s.loadedFromDate);

  // "Loading" means the currently viewed window hasn't streamed in yet: either no chunk
  // has arrived, or the window reaches further back than what's loaded so far. We do NOT
  // wait for the full background crawl (even on the all-time view) — a tile clears as soon
  // as its visible window [startDate, endDate] is covered and then refines in place as
  // older history keeps streaming, matching the charts which already render progressively.
  const isOrdersLoading =
    !ordersFullyLoaded &&
    (!ordersFirstChunkLoaded ||
      (!!ordersLoadedFromDate && startDate < ordersLoadedFromDate));

  const isRevenueLoading =
    !revenueFullyLoaded &&
    (!revenueFirstChunkLoaded ||
      (!!revenueLoadedFromDate && startDate < revenueLoadedFromDate));

  // Build revenue source → restaurant lookup for filtering revenue by restaurant
  const revSourceToRestaurant = useMemo(() => {
    const lookup: Record<string, string> = {};
    for (const [rId, sources] of Object.entries(revenueSourcesMap)) {
      for (const source of sources) {
        lookup[source.id] = rId;
      }
    }
    return lookup;
  }, [revenueSourcesMap]);

  // Restaurant-filtered base data (scopes all downstream calculations when filtering)
  const baseOrders = useMemo(() => {
    if (!isAllRestaurants || restaurantFilters.length === 0) return orderHistoryStore.orderHistoryList;
    return orderHistoryStore.orderHistoryList.filter(order => {
      const rId = order.restaurantSection?.restaurantId || order.restaurantId;
      return rId && restaurantFilters.includes(rId);
    });
  }, [orderHistoryStore.orderHistoryList, restaurantFilters, isAllRestaurants]);

  const baseRevenue = useMemo(() => {
    if (!isAllRestaurants || restaurantFilters.length === 0) return revenueHistoryStore.revenueHistoryList;
    return revenueHistoryStore.revenueHistoryList.filter(rev => {
      const rId = rev.restaurantId || revSourceToRestaurant[rev.restaurantRevenueSourceId];
      return rId && restaurantFilters.includes(rId);
    });
  }, [revenueHistoryStore.revenueHistoryList, restaurantFilters, isAllRestaurants, revSourceToRestaurant]);

  // Available restaurants for filter options
  const availableRestaurants = useMemo(() =>
    isAllRestaurants ? restaurants.map(r => ({ id: r.id, name: r.name })) : [],
    [isAllRestaurants, restaurants]
  );

  // Filter data by date range (uses restaurant-filtered base data)
  const dateFilteredOrders = useMemo(() =>
    filterOrdersByDateRange(baseOrders, startDate, endDate),
    [baseOrders, startDate, endDate]
  );

  const dateFilteredRevenue = useMemo(() => {
    return filterRevenueByDateRange(baseRevenue, startDate, endDate);
  }, [baseRevenue, startDate, endDate]);

  // Handler for overall type filter changes
  const handleOverallTypeChange = (value: string) => {
    setOverallTypeFilter(value as 'all' | 'food' | 'drinks');
  };

  // Handler for Revenue VAT toggle - updates revenue VAT setting
  const handleRevenueVatChange = async (value: string) => {
    const showExVat = value === 'excl';
    try {
      await saveVatSetting(showExVat);
    } catch (error) {
      console.error('Failed to save Revenue VAT setting:', error);
    }
  };

  // Handler for Orders VAT toggle - updates orders VAT setting
  const handleOrdersVatChange = async (value: string) => {
    const showExVat = value === 'excl';
    try {
      await saveVatOrdersSetting(showExVat);
    } catch (error) {
      console.error('Failed to save Orders VAT setting:', error);
    }
  };

  // Apply overall type filter on top of date filters
  const filteredOrders = useMemo(() =>
    filterOrdersByOverallType(dateFilteredOrders, overallTypeFilter),
    [dateFilteredOrders, overallTypeFilter]
  );

  const filteredRevenue = useMemo(() => {
    return filterRevenueByOverallType(dateFilteredRevenue, overallTypeFilter)
  }, [dateFilteredRevenue, overallTypeFilter]
  );

  // Calculate KPIs
  const totalRevenue = useMemo(() =>
    calculateTotalRevenue(filteredRevenue, showExVatRevenue),
    [filteredRevenue, showExVatRevenue]
  );

  const totalSpent = useMemo(() =>
    calculateTotalSpent(filteredOrders, showExVatOrders),
    [filteredOrders, showExVatOrders]
  );

  const costPercentage = useMemo(() =>
    calculateCostPercentage(totalSpent, totalRevenue),
    [totalSpent, totalRevenue]
  );

  // Food/drinks split (only shown when the overall type filter is 'all')
  const showSplit = overallTypeFilter === 'all';

  const revenueSplit = useMemo(() =>
    showSplit ? calculateRevenueMix(filteredRevenue, showExVatRevenue) : null,
    [showSplit, filteredRevenue, showExVatRevenue]
  );

  const spentSplit = useMemo(() => {
    if (!showSplit) return null;
    const foodSpent = calculateTotalSpent(filterOrdersByOverallType(dateFilteredOrders, 'food'), showExVatOrders);
    const drinksSpent = calculateTotalSpent(filterOrdersByOverallType(dateFilteredOrders, 'drinks'), showExVatOrders);
    const total = foodSpent + drinksSpent;
    return {
      foodSpent,
      drinksSpent,
      foodPct: total > 0 ? (foodSpent / total) * 100 : 0,
      drinksPct: total > 0 ? (drinksSpent / total) * 100 : 0,
    };
  }, [showSplit, dateFilteredOrders, showExVatOrders]);

  const costPercentageSplit = useMemo(() => {
    if (!showSplit || !revenueSplit || !spentSplit) return null;
    return {
      food: calculateCostPercentage(spentSplit.foodSpent, revenueSplit.food.value),
      drinks: calculateCostPercentage(spentSplit.drinksSpent, revenueSplit.drinks.value),
    };
  }, [showSplit, revenueSplit, spentSplit]);

  // Stock-adjusted cost %: single restaurant, ended week/month/quarter only.
  const stockAdjustedEnabled =
    !restaurantOverviewOnly &&
    !isAllRestaurants &&
    canAccessInventoryAdjustedCostPercent(backendUser, accountSettings) &&
    (selectedTimeOption === 'week' || selectedTimeOption === 'month' || selectedTimeOption === 'quarter');
  const stockRestaurantIds = useMemo(() => {
    return !isAllRestaurants && restaurantId ? [restaurantId] : [];
  }, [isAllRestaurants, restaurantId]);
  const stockAdjustment = useStockAdjustedCost({
    enabled: stockAdjustedEnabled,
    accountId: backendUser?.id,
    restaurantIds: stockRestaurantIds,
    startDate,
    endDate,
    orders: baseOrders,
    revenue: baseRevenue,
    historyReady: ordersFullyLoaded && revenueFullyLoaded,
    showExVatOrders,
  });
  const stockAdjustedLine = (stock: SideStockAdjustment | null | undefined, purchases: number, revenue: number, compact = false) => {
    if (!stock) return null;
    const percent = stockAdjustedCostPercent(purchases, revenue, stock);
    return percent === null ? null : <StockAdjustedCost percent={percent} stock={stock} compact={compact} />;
  };
  const singleSideStockLine = !showSplit && (overallTypeFilter === 'food' || overallTypeFilter === 'drinks')
    ? stockAdjustedLine(stockAdjustment?.[overallTypeFilter], totalSpent, totalRevenue)
    : null;

  // Apply overall type filter to base data for KPI change calculations
  const baseRevenueFiltered = useMemo(() =>
    filterRevenueByOverallType(baseRevenue, overallTypeFilter),
    [baseRevenue, overallTypeFilter]
  );

  const baseOrdersFiltered = useMemo(() =>
    filterOrdersByOverallType(baseOrders, overallTypeFilter),
    [baseOrders, overallTypeFilter]
  );

  // Calculate change values (using restaurant + overallType filtered base data)
  const revenueChange = useMemo(() =>
    calculateRevenueChange(
      totalRevenue,
      selectedTimeOption as View,
      startDate,
      endDate,
      baseRevenueFiltered,
      showExVatRevenue
    ),
    [totalRevenue, selectedTimeOption, startDate, endDate, baseRevenueFiltered, showExVatRevenue]
  );

  const costChange = useMemo(() =>
    calculateCostChange(
      totalSpent,
      selectedTimeOption as View,
      startDate,
      endDate,
      baseOrdersFiltered,
      showExVatOrders
    ),
    [totalSpent, selectedTimeOption, startDate, endDate, baseOrdersFiltered, showExVatOrders]
  );

  const costPercentageChangeData = useMemo(() =>
    calculateCostPercentageChange(
      costPercentage,
      selectedTimeOption as View,
      startDate,
      endDate,
      baseOrdersFiltered,
      baseRevenueFiltered,
      showExVatOrders,
      showExVatRevenue
    ),
    [costPercentage, selectedTimeOption, startDate, endDate, baseOrdersFiltered, baseRevenueFiltered, showExVatOrders, showExVatRevenue]
  );

  // Per-restaurant food cost target (falls back to BUDGET_TARGET_PERCENTAGE),
  // read from the cached restaurant store — no per-page fetch.
  const reportSettingsMap = useRestaurantStore(s => s.reportSettings);
  const foodCostTarget = useMemo(() => {
    if (!restaurantId || isAllRestaurants) return BUDGET_TARGET_PERCENTAGE;
    const food = reportSettingsMap[restaurantId]?.costTargets?.food;
    return typeof food === 'number' && food > 0 ? food : BUDGET_TARGET_PERCENTAGE;
  }, [restaurantId, isAllRestaurants, reportSettingsMap]);

  // Budget label state (food filter + week view + account setting only).
  // Budget math always uses ex-VAT so dashboard VAT toggles don't shift the
  // "remaining to spend" number under the user.
  const showBudgetLabel =
    canAccessBudgetReminder(backendUser, accountSettings) &&
    selectedTimeOption === 'week' &&
    overallTypeFilter === 'food' &&
    !isAllRestaurants &&
    !isOrdersLoading &&
    !isRevenueLoading;

  const totalSpentExVat = useMemo(
    () => calculateTotalSpent(filteredOrders, true),
    [filteredOrders],
  );
  const totalRevenueExVat = useMemo(
    () => calculateTotalRevenue(filteredRevenue, true),
    [filteredRevenue],
  );

  const budgetLabelData = useMemo(() => {
    if (!showBudgetLabel) return null;
    if (isCurrentWeek(startDate)) {
      const { remaining } = calculateCurrentWeekBudget(
        baseRevenueFiltered,
        totalSpentExVat,
        true,
        new Date(),
        foodCostTarget,
      );
      return { mode: 'current' as const, amount: remaining };
    }
    if (totalRevenueExVat <= 0) return null;
    const { difference } = calculatePastWeekDifference(totalSpentExVat, totalRevenueExVat, foodCostTarget);
    return { mode: 'past' as const, amount: difference };
  }, [showBudgetLabel, startDate, baseRevenueFiltered, totalSpentExVat, totalRevenueExVat, foodCostTarget]);

  // // Food-only supplier spending (for Ayla custom dashboard)
  // const foodOnlySupplierSpending = useMemo(() =>
  //   calculateFoodOnlySupplierSpending(filteredOrders, showExVatOrders),
  //   [filteredOrders, showExVatOrders]
  // );

  // Full supplier spending including non-food (for regular dashboard)
  const fullSupplierSpending = useMemo(() =>
    calculateFullSupplierSpending(filteredOrders, showExVatOrders),
    [filteredOrders, showExVatOrders]
  );

  // Calculate graph data (using restaurant-filtered base data)
  const revenueGraphData = useMemo(() =>
    calculateRevenueGraphData(
      baseRevenue,
      startDate,
      endDate,
      selectedTimeOption as 'day' | 'week' | 'month' | 'quarter' | 'all',
      showExVatRevenue
    ),
    [baseRevenue, startDate, endDate, selectedTimeOption, showExVatRevenue]
  );

  const costGraphData = useMemo(() =>
    calculateCostGraphData(
      baseOrders,
      startDate,
      endDate,
      selectedTimeOption as 'day' | 'week' | 'month' | 'quarter' | 'all',
      showExVatOrders
    ),
    [baseOrders, startDate, endDate, selectedTimeOption, showExVatOrders]
  );

  // Filter price alerts by date range
  const filteredPriceAlerts = useMemo(() =>
    filterPriceAlertsByDateRange(priceAlertsStore.priceAlerts, startDate, endDate),
    [priceAlertsStore.priceAlerts, startDate, endDate]
  );

  // Get platform info for transforming alerts
  const platformsForAlerts = useMemo(() => {
    const sections = isAllRestaurants
      ? Object.values(restaurantSectionsMap).flat()
      : (restaurantSectionsMap[restaurantId || ''] || []);

    const platforms = sections.flatMap(s => platformMap[s.id] || []);
    const uniquePlatforms = Array.from(new Map(platforms.map(p => [p.id, p])).values());

    return new Map(uniquePlatforms.map(p => [p.id, p]));
  }, [isAllRestaurants, restaurantSectionsMap, restaurantId, platformMap]);

  // Transform price alerts for the card component
  const priceAlertsForCard = useMemo(() =>
    transformPriceAlertsForCard(filteredPriceAlerts, platformsForAlerts),
    [filteredPriceAlerts, platformsForAlerts]
  );

  // Calculate top revenue items (show top 8 for the table with pagination)
  const topRevenueItems = useMemo(() => {
    return calculateTopRevenueItems(filteredRevenue, showExVatRevenue)
  }, [filteredRevenue, showExVatRevenue]
  );

  // Use food-only supplier spending if account has the setting, full spending otherwise
  // const supplierSpendingData = hasDashboardFoodOnly(backendUser, accountSettings) ? foodOnlySupplierSpending : fullSupplierSpending;
  const supplierSpendingData = fullSupplierSpending;

  // Per-restaurant food-cost target (%) for the overview cost% pill (own target → red/green).
  const costTargetByRestaurant = useMemo(() => {
    const map: Record<string, number> = {};
    for (const r of restaurants) {
      const food = reportSettingsMap[r.id]?.costTargets?.food;
      if (typeof food === 'number' && food > 0) map[r.id] = food;
    }
    return map;
  }, [restaurants, reportSettingsMap]);

  // All-restaurants overview data
  const restaurantOverviewData = useMemo(() => {
    if (!isAllRestaurants) return [];
    return calculateRestaurantOverview(
      filteredOrders,
      filteredRevenue,
      baseOrdersFiltered,
      baseRevenueFiltered,
      restaurants,
      revenueSourcesMap,
      showExVatOrders,
      showExVatRevenue,
      selectedTimeOption as View,
      startDate,
      endDate,
      costTargetByRestaurant,
      BUDGET_TARGET_PERCENTAGE
    );
  }, [isAllRestaurants, filteredOrders, filteredRevenue, baseOrdersFiltered, baseRevenueFiltered, restaurants, revenueSourcesMap, showExVatOrders, showExVatRevenue, selectedTimeOption, startDate, endDate, costTargetByRestaurant]);

  const restaurantRevenueBreakdown = useMemo(() => {
    if (!isAllRestaurants) return [];
    return calculateRestaurantRevenueBreakdown(
      filteredRevenue,
      restaurants,
      revenueSourcesMap,
      showExVatRevenue
    );
  }, [isAllRestaurants, filteredRevenue, restaurants, revenueSourcesMap, showExVatRevenue]);

  const restaurantCostBreakdown = useMemo(() => {
    if (!isAllRestaurants) return [];
    return calculateRestaurantCostBreakdown(
      filteredOrders,
      restaurants,
      showExVatOrders
    );
  }, [isAllRestaurants, filteredOrders, restaurants, showExVatOrders]);

  // ── All-restaurants ranking cards ────────────────────────────────────────
  // Waste ranking: the server ships a period-INDEPENDENT per-unit "needed ingredients"
  // index per restaurant (fetched once via useLoadWasteNeeds); we compute waste % for the
  // selected period here, client-side, from that index + the orders/revenue already
  // loaded for the page. Stepping the time controls re-computes locally — no server call.
  const wasteNeeds = useWasteNeedsStore(s => s.needs);
  // `loadedKey` becomes non-null only after the needs index has actually loaded. Gate on
  // that (not the transient `isLoading`) AND on the selected period's order/revenue still
  // streaming — the needs index returns almost instantly, so without this the card would
  // flash "no data in period" while the page's orders/revenue are still loading.
  const wasteNeedsReady = useWasteNeedsStore(s => s.loadedKey !== null);
  const wasteLoading = !wasteNeedsReady || isOrdersLoading || isRevenueLoading;

  const wasteRange = useMemo<ReportRange>(
    () => ({ start: startDate, end: endDate, comparisonHidden: true }),
    [startDate, endDate]
  );

  const wasteRows = useMemo(() => {
    if (!isAllRestaurants) return [];
    // Group the (restaurant-filtered, overall-type-filtered, full-history) base data by
    // restaurant — same restaurant resolution the page uses for baseOrders/baseRevenue.
    const ordersByRest = new Map<string, typeof baseOrdersFiltered>();
    for (const o of baseOrdersFiltered) {
      const rId = o.restaurantSection?.restaurantId || o.restaurantId;
      if (!rId) continue;
      const list = ordersByRest.get(rId); if (list) list.push(o); else ordersByRest.set(rId, [o]);
    }
    const revByRest = new Map<string, typeof baseRevenueFiltered>();
    for (const r of baseRevenueFiltered) {
      const rId = r.restaurantId || revSourceToRestaurant[r.restaurantRevenueSourceId];
      if (!rId) continue;
      const list = revByRest.get(rId); if (list) list.push(r); else revByRest.set(rId, [r]);
    }
    const rows = [];
    for (const restaurantId of Object.keys(wasteNeeds)) {
      // Honour the restaurant filter (baseOrders is already filtered, but a filtered-out
      // restaurant would otherwise show as a 0% row).
      if (restaurantFilters.length > 0 && !restaurantFilters.includes(restaurantId)) continue;
      const rn = wasteNeeds[restaurantId];
      const totals = computeWasteTotalsFromNeeds({
        perUnit: rn.perUnit,
        orders: ordersByRest.get(restaurantId) ?? [],
        revenue: revByRest.get(restaurantId) ?? [],
        range: wasteRange,
      });
      rows.push({
        restaurantId,
        restaurantName: rn.restaurantName,
        wasteEur: totals.totalPotentialWasteEur,
        periodSpend: totals.periodSpend,
        wastePercent: totals.wastePercent,
      });
    }
    return rows;
  }, [isAllRestaurants, wasteNeeds, baseOrdersFiltered, baseRevenueFiltered, revSourceToRestaurant, restaurantFilters, wasteRange]);

  const [wasteModalOpen, setWasteModalOpen] = useState(false);
  const [revenueModalOpen, setRevenueModalOpen] = useState(false);
  const [costModalOpen, setCostModalOpen] = useState(false);

  // Revenue ranking: restaurant overview rows by revenue desc (full list; card shows top 5).
  // Modal rows carry a proportional bar (share of total revenue) like the revenue-by-restaurant card.
  const revenueRankingFull = useMemo<RankingModalRow[]>(() =>
    [...restaurantOverviewData]
      .sort((a, b) => b.revenue - a.revenue)
      .map(r => ({
        name: r.restaurantName,
        value: currencyFormatter.format(r.revenue),
        barPct: r.revenueShare,
        sub: formatPct(r.revenueShare),
      })),
    [restaurantOverviewData]
  );
  const revenueRankingItems = useMemo(() => revenueRankingFull.slice(0, 5), [revenueRankingFull]);

  // Highest cost % ranking: rows by costPercent desc (full list; card shows top 5).
  const costPercentRankingFull = useMemo<RankingModalRow[]>(() =>
    [...restaurantOverviewData]
      .filter(r => r.revenue > 0)
      .sort((a, b) => b.costPercent - a.costPercent)
      .map(r => ({
        name: r.restaurantName,
        value: formatPct(r.costPercent),
        barPct: Math.min(r.costPercent, 100),
        barColor: r.costTarget > 0 && r.costPercent > r.costTarget ? '#eb8d00' : '#0E8965',
      })),
    [restaurantOverviewData]
  );
  const costPercentRankingItems = useMemo(() => costPercentRankingFull.slice(0, 5), [costPercentRankingFull]);

  // Product spend: per-product aggregation across the period — how many distinct restaurants
  // ordered each product, and the total spend on it.
  const productSpendAgg = useMemo(() => {
    if (!isAllRestaurants) return [];
    // Use the SAME grouping key + spend formula as the OrderHistoryProductsPage (source of
    // truth) so the dashboard top 5 matches what the products page shows: per-platform key,
    // VAT-aware price, and per-kg-aware quantity.
    const map = new Map<string, { name: string; restaurantIds: Set<string>; total: number }>();
    for (const order of filteredOrders) {
      const rId = order.restaurantSection?.restaurantId || order.restaurantId || 'unknown';
      for (const p of order.productData || []) {
        const keyInfo = getProductAggregationKey(p, order.platform);
        if (!keyInfo) continue;
        let entry = map.get(keyInfo.key);
        if (!entry) { entry = { name: p.name, restaurantIds: new Set<string>(), total: 0 }; map.set(keyInfo.key, entry); }
        if (rId !== 'unknown') entry.restaurantIds.add(rId);
        entry.total += getProductPrice(p, showExVatOrders) * getProductQuantity(p, order.platform);
      }
    }
    return Array.from(map.values())
      .map(e => ({ name: e.name, restaurantCount: e.restaurantIds.size, total: e.total }))
      .sort((a, b) => b.total - a.total);
  }, [isAllRestaurants, filteredOrders, showExVatOrders]);

  // Show spend rounded to whole euros to leave room for product names.
  const productSpendRankingItems = useMemo(() =>
    productSpendAgg.slice(0, 5).map(p => ({ name: p.name, value: wholeCurrencyFormatter.format(p.total) })),
    [productSpendAgg]
  );

  // Waste ranking: waste-summary rows by wastePercent desc.
  const sortedWasteRows = useMemo(() =>
    [...wasteRows].sort((a, b) => b.wastePercent - a.wastePercent),
    [wasteRows]
  );

  const wasteRankingItems = useMemo(() =>
    sortedWasteRows
      .slice(0, 5)
      .map(r => ({ name: r.restaurantName, value: formatPct(r.wastePercent * 100) })),
    [sortedWasteRows]
  );

  // Shared rank-badge + name + value rows for the "view full ranking" modals. Rows with a
  // `barPct` render a proportional bar (+ optional secondary text) beneath the name/value line.
  const renderRankingRows = (items: RankingModalRow[]) =>
    items.length === 0 ? (
      <NoDataInPeriod />
    ) : (
      <div className="flex flex-col">
        {items.map((item, i) => (
          <div
            key={`${i}-${item.name}`}
            className="flex items-center gap-3 px-2 py-2.5 border-b border-border dark:border-border-dark"
          >
            <span
              className="w-5 h-5 rounded-full flex items-center justify-center text-white dark:text-white text-[11px] font-bold flex-shrink-0"
              style={getRankBadgeStyle(i + 1)}
            >
              {i + 1}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-textPrimary dark:text-textPrimary-dark truncate">
                  {item.name}
                </span>
                <span className="flex items-center gap-2 flex-shrink-0">
                  {item.sub && (
                    <span className="text-xs font-medium text-textSecondary dark:text-textSecondary-dark">
                      {item.sub}
                    </span>
                  )}
                  <span className="text-sm font-semibold text-textPrimary dark:text-textPrimary-dark">
                    {item.value}
                  </span>
                </span>
              </div>
              {typeof item.barPct === 'number' && (
                <div className="mt-1.5 w-full h-1.5 bg-[#F2F4F7] dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${Math.min(item.barPct, 100)}%`, backgroundColor: item.barColor ?? '#7047EB' }}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );

  if (restaurantOverviewOnly && selectedRestaurant && !isAllRestaurants) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div>
      <MobilePageHeader title={t(restaurantOverviewOnly ? 'dashboard.restaurantOverview' : 'nav.home')} right={<RestaurantSelector />} />

      <TopSection
        title={restaurantOverviewOnly ? t('dashboard.restaurantOverview') : `${t('dashboard.welcomeBack')}, ${backendUser?.name ?? ""}`}
        description={restaurantOverviewOnly ? undefined : t('dashboard.salesSummaryDescription')}
        backButton={restaurantOverviewOnly}
        handleGoBack={() => navigate('/dashboard')}
        dateRange={dateRange}
        setDateRange={setDateRange}
        selectedTimeOption={selectedTimeOption}
        setSelectedTimeOption={setSelectedTimeOption}
        onOverallTypeChange={handleOverallTypeChange}
        currentOverallTypeValue={overallTypeFilter}
        overallTypeOptions={['all', 'food', 'drinks']}
        onRevenueVatChange={handleRevenueVatChange}
        currentRevenueVatValue={showExVatRevenue ? 'excl' : 'incl'}
        onOrdersVatChange={handleOrdersVatChange}
        currentOrdersVatValue={showExVatOrders ? 'excl' : 'incl'}
        rightControls={isAllRestaurants ? (
          <RestaurantFilterDropdown
            restaurants={availableRestaurants}
            selectedRestaurantIds={restaurantFilters}
            setSelectedRestaurantIds={setRestaurantFilters}
          />
        ) : undefined}
        collapseSecondaryControlsOnMobile
      />

      {/* Responsive layout group for content sections below 1250px */}
      <div className="mt-4 mb-4 flex flex-col gap-6 min-[1250px]:flex-col">
        {/* KPI Cards, row on >=1250px, column below (compact rows, tighter gap on phones) */}
        <div className="flex flex-col gap-3 sm:gap-6 min-[1250px]:flex-row">
          <div className="w-full min-[1250px]:flex-1" data-onboarding="kpi-revenue page-kpis" data-onboarding-ready={!isRevenueLoading}>
            <StatCard
              {...metricIcon('revenue')}
              title={t('dashboard.totalRevenue')}
              value={totalRevenue}
              valueType="currency"
              changeValue={revenueChange?.changeValue}
              changeLabel={revenueChange?.changeLabel}
              trendDirection="up"
              onClick={() => navigate('/revenuehistory')}
              loading={isRevenueLoading}
              bottom={revenueSplit && (revenueSplit.food.value + revenueSplit.drinks.value) > 0 ? (
                <SplitLegend rows={[
                  { color: SPLIT_FOOD, label: t('dashboard.food'), value: `${formatPct(revenueSplit.food.percentage)} · ${currencyFormatter.format(revenueSplit.food.value)}` },
                  { color: SPLIT_DRINKS, label: t('dashboard.drinks'), value: `${formatPct(revenueSplit.drinks.percentage)} · ${currencyFormatter.format(revenueSplit.drinks.value)}` },
                ]} />
              ) : undefined}
            />
          </div>
          <div className="w-full min-[1250px]:flex-1 relative" data-onboarding="kpi-spend page-kpis" data-onboarding-ready={!isOrdersLoading}>
            <StatCard
              {...metricIcon('spend')}
              title={t('dashboard.totalSpent')}
              value={totalSpent}
              valueType="currency"
              changeValue={costChange?.changeValue}
              changeLabel={costChange?.changeLabel}
              trendDirection="down"
              onClick={() => navigate('/orderhistory')}
              loading={isOrdersLoading}
              bottom={spentSplit && (spentSplit.foodSpent + spentSplit.drinksSpent) > 0 ? (
                <SplitLegend rows={[
                  { color: SPLIT_FOOD, label: t('dashboard.food'), value: `${formatPct(spentSplit.foodPct)} · ${currencyFormatter.format(spentSplit.foodSpent)}` },
                  { color: SPLIT_DRINKS, label: t('dashboard.drinks'), value: `${formatPct(spentSplit.drinksPct)} · ${currencyFormatter.format(spentSplit.drinksSpent)}` },
                ]} />
              ) : undefined}
            />
            {budgetLabelData && (
              // Hidden on phones: the compact card's change pill occupies the top-right.
              <div className="hidden sm:block absolute top-3 right-3 z-10">
                <BudgetLabel
                  mode={budgetLabelData.mode}
                  amount={budgetLabelData.amount}
                  targetPercentage={foodCostTarget}
                />
              </div>
            )}
          </div>
          <div className="w-full min-[1250px]:flex-1" data-onboarding="kpi-cost page-kpis" data-onboarding-ready={!isOrdersLoading && !isRevenueLoading}>
            <StatCard
              {...metricIcon('costPercent')}
              title={t('dashboard.costPercentage')}
              value={costPercentage}
              valueType="percentage"
              changeValue={costPercentageChangeData?.changeValue}
              changeLabel={costPercentageChangeData?.changeLabel}
              trendDirection="down"
              onClick={() => navigate(canAccessCostPercentPage(backendUser, accountSettings) ? '/costpercent' : '/costprice')}
              loading={isOrdersLoading || isRevenueLoading}
              bottom={costPercentageSplit && (costPercentageSplit.food > 0 || costPercentageSplit.drinks > 0) ? (
                <SplitLegend rows={[
                  { color: SPLIT_FOOD, label: t('dashboard.food'), value: formatPct(costPercentageSplit.food),
                    extra: spentSplit && revenueSplit ? stockAdjustedLine(stockAdjustment?.food, spentSplit.foodSpent, revenueSplit.food.value, true) : null },
                  { color: SPLIT_DRINKS, label: t('dashboard.drinks'), value: formatPct(costPercentageSplit.drinks),
                    extra: spentSplit && revenueSplit ? stockAdjustedLine(stockAdjustment?.drinks, spentSplit.drinksSpent, revenueSplit.drinks.value, true) : null },
                ]} />
              ) : singleSideStockLine ?? undefined}
            />
          </div>
        </div>

        {restaurantOverviewOnly && (
          <RestaurantOverviewTable
            fullPage
            data={restaurantOverviewData}
            loading={isOrdersLoading || isRevenueLoading}
            restaurantFilter={{
              selectedValues: restaurantFilters,
              onValuesChange: setRestaurantFilters,
              availableOptions: availableRestaurants,
            }}
          />
        )}

        {/* Ranking cards (all-restaurants only) */}
        {!restaurantOverviewOnly && isAllRestaurants && (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
            <RankingCard
              icon={<RevenueIcon className="w-5 h-5" />}
              title={t('dashboard.revenue')}
              items={revenueRankingItems}
              onViewFull={() => setRevenueModalOpen(true)}
              loading={isRevenueLoading}
            />
            <RankingCard
              icon={<SpendIcon className="w-5 h-5" />}
              title={t('dashboard.highestCostPercent')}
              items={costPercentRankingItems}
              onViewFull={() => setCostModalOpen(true)}
              loading={isOrdersLoading || isRevenueLoading}
            />
            <RankingCard
              icon={<ProductSpendIcon className="w-5 h-5" />}
              title={t('dashboard.productSpend')}
              items={productSpendRankingItems}
              onViewFull={() => navigate('/orderhistory/products')}
              loading={isOrdersLoading}
            />
            <RankingCard
              icon={<WasteIcon className="w-5 h-5" />}
              title={t('dashboard.waste')}
              items={wasteRankingItems}
              onViewFull={() => setWasteModalOpen(true)}
              loading={wasteLoading}
            />
          </div>
        )}

        {/* Graph & sidebar chart: row on >=1250px, column below */}
        {!restaurantOverviewOnly && (isAllRestaurants ? (
          <div className="flex flex-col gap-6 min-[1250px]:flex-row">
            <div className="w-full min-[1250px]:w-[67.5%]">
              <RestaurantOverviewTable
                data={restaurantOverviewData}
                loading={isOrdersLoading || isRevenueLoading}
                restaurantFilter={{
                  selectedValues: restaurantFilters,
                  onValuesChange: setRestaurantFilters,
                  availableOptions: availableRestaurants,
                }}
              />
            </div>
            <div className="w-full min-[1250px]:w-[32.5%]">
              <RestaurantRevenuePieChart
                revenueData={restaurantRevenueBreakdown}
                costData={restaurantCostBreakdown}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-6 min-[1250px]:flex-row">
            <div className="w-full min-[1250px]:w-[67.5%]">
              <GraphCard
                title={t('dashboard.revenueSpendTrends')}
                xLabels={revenueGraphData.map(d => d.label)}
                series={[
                  { label: t('dashboard.revenue'), values: revenueGraphData.map(d => d.value), color: graphPurpleColor },
                  { label: t('dashboard.spend'), values: costGraphData.map(d => d.value), color: graphOrangeColor }
                ]}
                height={330}
              />
            </div>
            <div className="w-full min-[1250px]:w-[32.5%]">
              <SideBarChart
                data={supplierSpendingData}
                title={t('dashboard.supplierSpending')}
                seeAllText={t('dashboard.seeAll')}
                onSeeAllClick={() => {navigate('/orderhistory')}}
              />
            </div>
          </div>
        ))}

        {/* Alerts & Table: single-restaurant only (dropped in all-restaurants per mock) */}
        {!restaurantOverviewOnly && !isAllRestaurants && (
          <div className="flex flex-col gap-6 min-[1250px]:flex-row">
            <div className="min-[1250px]:w-5/12 w-full">
              <PriceAlertsCard
                data={priceAlertsForCard}
                onSeeAllClick={() => {navigate('/pricealerts')}}
              />
            </div>
            <div className="min-[1250px]:w-7/12 w-full">
              <ResponsiveTable
                desktop={
                  <TableComponent
                    title={t('dashboard.topRevenueItems')}
                    data={topRevenueItems}
                    blankLines={true}
                    columns={[
                      { key: 'product', label: t('dashboard.product'), columnType: 'name' },
                      { key: 'items', label: t('dashboard.items'), columnType: 'blueNumber' },
                      { key: 'price', label: t('dashboard.price'), columnType: 'currency' },
                      { key: 'totalPrice', label: t('dashboard.totalPrice'), columnType: 'currencyHighlight' },
                    ]}
                    seeAllText={t('dashboard.seeAll')}
                    onSeeAllClick={() => {navigate('/revenuehistory/items')}}
                  />
                }
                mobile={
                  /* Mobile: top-3 item cards + "View All" to the full items page */
                  <div>
                    <h3 className="text-[18px] leading-6 font-semibold text-textPrimary dark:text-textPrimary-dark mb-4">
                      {t('dashboard.topRevenueItems')}
                    </h3>
                    <MobileCardList
                      items={topRevenueItems}
                      keyOf={(item, i) => `${item.product}-${i}`}
                      previewCount={3}
                      onViewAll={() => navigate('/revenuehistory/items')}
                      viewAllLabel={t('dashboard.seeAll')}
                      renderCard={(item) => (
                        <div className="bg-white dark:bg-card-dark rounded-xl p-4 shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]">
                          <div className="text-[14px] font-semibold text-textPrimary dark:text-textPrimary-dark mb-3 truncate">
                            {item.product}
                          </div>
                          <div className="flex items-stretch border-t border-border dark:border-border-dark pt-3">
                            <MobileCardField label={t('dashboard.items')} value={String(item.items)} divider />
                            <MobileCardField label={t('dashboard.price')} value={currencyFormatter.format(item.price)} divider />
                            <MobileCardField label={t('dashboard.totalPrice')} value={currencyFormatter.format(item.totalPrice)} />
                          </div>
                        </div>
                      )}
                    />
                  </div>
                }
              />
            </div>
          </div>
        )}
      </div>

      {/* Full revenue ranking (all-restaurants) */}
      <ExpandModal
        isOpen={revenueModalOpen}
        onClose={() => setRevenueModalOpen(false)}
        title={t('dashboard.revenue')}
        maxWidth="max-w-2xl"
      >
        {renderRankingRows(revenueRankingFull)}
      </ExpandModal>

      {/* Full highest-cost-% ranking (all-restaurants) */}
      <ExpandModal
        isOpen={costModalOpen}
        onClose={() => setCostModalOpen(false)}
        title={t('dashboard.highestCostPercent')}
        maxWidth="max-w-2xl"
      >
        {renderRankingRows(costPercentRankingFull)}
      </ExpandModal>

      {/* Full waste ranking (all-restaurants) */}
      <ExpandModal
        isOpen={wasteModalOpen}
        onClose={() => setWasteModalOpen(false)}
        title={t('dashboard.wasteRanking')}
        maxWidth="max-w-2xl"
      >
        {sortedWasteRows.length === 0 ? (
          <NoDataInPeriod />
        ) : (
          <div className="flex flex-col">
            <div className="flex items-center gap-3 px-2 py-2 border-b border-border dark:border-border-dark text-xs font-semibold text-muted dark:text-muted-dark uppercase">
              <span className="w-5 flex-shrink-0" />
              <span className="flex-1">{t('dashboard.restaurant')}</span>
              <span className="w-20 text-right">{t('dashboard.waste')}</span>
              <span className="w-24 text-right">{t('dashboard.wasteValue')}</span>
            </div>
            {sortedWasteRows.map((row, i) => (
              <div key={row.restaurantId} className="flex items-center gap-3 px-2 py-2.5 border-b border-border dark:border-border-dark">
                <span
                  className="w-5 h-5 rounded-full flex items-center justify-center text-white dark:text-white text-[11px] font-bold flex-shrink-0"
                  style={getRankBadgeStyle(i + 1)}
                >
                  {i + 1}
                </span>
                <span className="flex-1 text-sm font-medium text-textPrimary dark:text-textPrimary-dark truncate">
                  {row.restaurantName}
                </span>
                <span className="w-20 text-right text-sm font-semibold text-textPrimary dark:text-textPrimary-dark">
                  {formatPct(row.wastePercent * 100)}
                </span>
                <span className="w-24 text-right text-sm text-textSecondary dark:text-textSecondary-dark">
                  {currencyFormatter.format(row.wasteEur)}
                </span>
              </div>
            ))}
          </div>
        )}
      </ExpandModal>
    </div>
  );
};

export default DashboardPage;
